// src/features/reservations/lib/useReservationDetail.ts
//
// Fetches the reservation detail (six facts cards) and its timeline together
// — both mockup 04 panels are shown at once, so one hook, one loading state.
import { useCallback, useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import type { ReservationDetailResponse, TimelineResponse } from '@/types/reservationState';

export interface UseReservationDetailResult {
  detail: ReservationDetailResponse | null;
  timeline: TimelineResponse | null;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

export const useReservationDetail = (id: string | null): UseReservationDetailResult => {
  const dataProvider = useDataProvider();
  const [detail, setDetail] = useState<ReservationDetailResponse | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchOnce = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(false);
    try {
      const [detailResult, timelineResult] = await Promise.all([
        dataProvider.getReservationDetail(id),
        dataProvider.getReservationTimeline(id),
      ]);
      setDetail(detailResult.data);
      setTimeline(timelineResult.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, id]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { detail, timeline, loading, error, refresh: () => void fetchOnce() };
};

export default useReservationDetail;
